import { useState, useEffect } from 'react'
import { Users as UsersIcon, Plus, Mail, Shield, Trash2, Edit, X, AlertTriangle, Crown, Send, Check, Zap, Rocket, Users } from 'lucide-react'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api'

// Plans tarifaires avec limites d'utilisateurs
const PLAN_LIMITS = {
  free: { name: 'Gratuit', maxUsers: 3, color: 'gray' },
  starter: { name: 'Starter', maxUsers: 10, color: 'blue' },
  professional: { name: 'Professionnel', maxUsers: 50, color: 'purple' },
  enterprise: { name: 'Entreprise', maxUsers: Infinity, color: 'gold' }
}

export default function TeamSettings({ subMenu }) {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'utilisateur'
  })
  const [tempPassword, setTempPassword] = useState(null)
  const [currentPlan, setCurrentPlan] = useState('free') // TODO: Récupérer du backend
  const { hasRole, user } = useAuth()

  // Dependency management states
  const [dependencies, setDependencies] = useState(null)
  const [loadingDependencies, setLoadingDependencies] = useState(false)
  const [dependencyActions, setDependencyActions] = useState({
    projects: { action: 'reassign', targetUserId: '' },
    annotations: { action: 'reassign', targetUserId: '' }
  })
  const [availableUsers, setAvailableUsers] = useState([])

  const planLimit = PLAN_LIMITS[currentPlan]
  const isAtLimit = users.length >= planLimit.maxUsers
  const usagePercentage = (users.length / planLimit.maxUsers) * 100

  useEffect(() => {
    if (hasRole(['responsable', 'super_admin'])) {
      fetchUsers()
    }
  }, [])

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      // Le backend retourne { success: true, data: { users } }
      setUsers(response.data?.data?.users || [])
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error)
      setUsers([]) // Initialiser avec un tableau vide en cas d'erreur
    } finally {
      setLoading(false)
    }
  }

  const handleAddUser = async (e) => {
    e.preventDefault()
    try {
      const response = await axios.post(`${API_URL}/users`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.data.success) {
        setTempPassword(response.data.data.tempPassword)
        await fetchUsers()
        // Ne pas fermer la modale immédiatement pour afficher le mot de passe
      }
    } catch (error) {
      console.error('Erreur lors de la création de l\'utilisateur:', error)
      alert(error.response?.data?.message || 'Erreur lors de la création de l\'utilisateur')
    }
  }

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const response = await axios.put(`${API_URL}/users/${userId}/role`,
        { role: newRole },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      )

      if (response.data.success) {
        await fetchUsers()
        setShowEditModal(false)
        setSelectedUser(null)
      }
    } catch (error) {
      console.error('Erreur lors de la modification du rôle:', error)
      alert(error.response?.data?.message || 'Erreur lors de la modification du rôle')
    }
  }

  const handleDeleteUser = async () => {
    if (!selectedUser) return

    try {
      // Step 1: Handle dependencies if any exist
      if (dependencies && dependencies.total > 0) {
        // Validate that all dependencies have actions selected
        if (dependencies.projects.count > 0) {
          if (dependencyActions.projects.action === 'reassign' && !dependencyActions.projects.targetUserId) {
            alert('Veuillez sélectionner un utilisateur pour réassigner les projets')
            return
          }
        }
        if (dependencies.annotations.count > 0) {
          if (dependencyActions.annotations.action === 'reassign' && !dependencyActions.annotations.targetUserId) {
            alert('Veuillez sélectionner un utilisateur pour réassigner les annotations')
            return
          }
        }

        // Prepare reassignment payload
        const reassignPayload = {
          targetUserId: dependencyActions.projects.targetUserId || dependencyActions.annotations.targetUserId,
          reassignProjects: dependencyActions.projects.action === 'reassign' && dependencies.projects.count > 0,
          deleteProjects: dependencyActions.projects.action === 'delete' && dependencies.projects.count > 0,
          reassignAnnotations: dependencyActions.annotations.action === 'reassign' && dependencies.annotations.count > 0,
          deleteAnnotations: dependencyActions.annotations.action === 'delete' && dependencies.annotations.count > 0
        }

        // Call reassignment endpoint
        await axios.post(`${API_URL}/users/${selectedUser.id}/reassign`, reassignPayload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
      }

      // Step 2: Delete the user
      const response = await axios.delete(`${API_URL}/users/${selectedUser.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.data.success) {
        await fetchUsers()
        setShowDeleteModal(false)
        setSelectedUser(null)
        setDependencies(null)
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', error)
      alert(error.response?.data?.message || 'Erreur lors de la suppression de l\'utilisateur')
    }
  }

  const resetForm = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      role: 'utilisateur'
    })
    setTempPassword(null)
    setShowAddModal(false)
  }

  const openEditModal = (user) => {
    setSelectedUser(user)
    setShowEditModal(true)
  }

  const openDeleteModal = async (user) => {
    setSelectedUser(user)
    setLoadingDependencies(true)
    setDependencies(null)
    setShowDeleteModal(true)

    // Fetch dependencies
    try {
      const response = await axios.get(`${API_URL}/users/${user.id}/dependencies`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      setDependencies(response.data.data)

      // Reset dependency actions
      setDependencyActions({
        projects: { action: 'reassign', targetUserId: '' },
        annotations: { action: 'reassign', targetUserId: '' }
      })

      // Get available users for reassignment (all users except the one being deleted)
      setAvailableUsers(users.filter(u => u.id !== user.id))
    } catch (error) {
      console.error('Erreur lors du chargement des dépendances:', error)
      setDependencies({ total: 0, projects: { count: 0 }, annotations: { count: 0 }, mentions: { count: 0 } })
    } finally {
      setLoadingDependencies(false)
    }
  }

  const handleAddUserClick = () => {
    if (isAtLimit) {
      setShowUpgradeModal(true)
    } else {
      setShowAddModal(true)
    }
  }

  const handleUpgradePlan = async () => {
    if (hasRole(['super_admin', 'responsable'])) {
      // Admin peut upgrader directement
      // TODO: Rediriger vers la page de tarification
      window.location.href = '/pricing'
    } else {
      // Utilisateur normal envoie une notification à l'admin
      try {
        await axios.post(`${API_URL}/notifications/request-upgrade`, {
          message: `Demande de passage à un plan supérieur pour ajouter plus d'utilisateurs`
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        alert('Notification envoyée à l\'administrateur')
        setShowUpgradeModal(false)
      } catch (error) {
        console.error('Erreur lors de l\'envoi de la notification:', error)
        alert('Erreur lors de l\'envoi de la demande')
      }
    }
  }

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: 'badge badge-danger',
      responsable: 'badge badge-warning',
      enseignant: 'badge badge-primary',
      collaborateur: 'badge badge-neutral',
      utilisateur: 'badge badge-neutral'
    }
    const labels = {
      super_admin: 'Super Admin',
      responsable: 'Responsable',
      enseignant: 'Enseignant',
      collaborateur: 'Utilisateur',
      utilisateur: 'Utilisateur'
    }
    return { class: badges[role] || 'badge badge-neutral', label: labels[role] || role }
  }

  if (!hasRole(['responsable', 'super_admin'])) {
    return (
      <div className="card">
        <div className="empty-state">
          <Shield className="empty-state-icon" />
          <h3 className="empty-state-title">Accès restreint</h3>
          <p className="empty-state-text">
            Vous n'avez pas les permissions nécessaires pour accéder à cette section.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="card">
        <div className="card-header p-6">
          <div>
            <h3 className="card-title">Équipe & Permissions</h3>
            <p className="card-subtitle">Gérez les utilisateurs et leurs permissions</p>
          </div>
          <button
            className={`btn ${isAtLimit ? 'btn-disabled' : 'btn-primary'}`}
            onClick={handleAddUserClick}
            disabled={isAtLimit && !hasRole(['super_admin', 'responsable'])}
          >
            <Plus className="h-4 w-4" />
            {isAtLimit ? 'Limite atteinte' : 'Ajouter un utilisateur'}
          </button>
        </div>
      </div>

      {/* Progressbar du plan */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <UsersIcon className="h-5 w-5 text-gray-600" />
            <h4 className="font-medium text-gray-900">Utilisation du plan {planLimit.name}</h4>
          </div>
          <div className="text-sm text-muted">
            <span className="font-semibold text-gray-900">{users.length}</span> / {planLimit.maxUsers === Infinity ? '∞' : planLimit.maxUsers} utilisateurs
          </div>
        </div>

        {/* Barre de progression */}
        <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`absolute top-0 left-0 h-full transition-all duration-500 ${
              usagePercentage >= 90
                ? 'bg-red-500'
                : usagePercentage >= 70
                ? 'bg-orange-500'
                : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          />
        </div>

        {/* Messages d'avertissement */}
        {isAtLimit && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-900">Limite atteinte</p>
              <p className="text-sm text-red-700 mt-1">
                Vous avez atteint la limite de {planLimit.maxUsers} utilisateurs de votre plan {planLimit.name}.
                {hasRole(['super_admin', 'responsable'])
                  ? ' Passez à un plan supérieur pour ajouter plus d\'utilisateurs.'
                  : ' Contactez votre administrateur pour augmenter la limite.'}
              </p>
              {hasRole(['super_admin', 'responsable']) && (
                <button
                  className="mt-3 btn btn-sm btn-danger"
                  onClick={() => setShowUpgradeModal(true)}
                >
                  <Crown className="h-4 w-4" />
                  Passer au plan supérieur
                </button>
              )}
            </div>
          </div>
        )}

        {usagePercentage >= 80 && !isAtLimit && (
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-orange-900">Attention</p>
              <p className="text-sm text-orange-700">
                Vous approchez de la limite de votre plan. Pensez à upgrader pour éviter les interruptions.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card card-kpi">
          <div className="kpi-label">Total utilisateurs</div>
          <div className="kpi-value">{users.length}</div>
          <div className="kpi-delta positive">
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7 14l5-5 5 5z"/>
            </svg>
            +12% ce mois
          </div>
        </div>

        <div className="card card-kpi">
          <div className="kpi-label">Admins</div>
          <div className="kpi-value">
            {users.filter(u => u.role === 'super_admin' || u.role === 'responsable').length}
          </div>
        </div>

        <div className="card card-kpi">
          <div className="kpi-label">Utilisateurs actifs</div>
          <div className="kpi-value">
            {users.filter(u => u.isActive).length}
          </div>
        </div>
      </div>

      {/* Table des utilisateurs */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th>Email</th>
              <th>Rôle</th>
              <th>Date création</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center p-8">
                  Chargement...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center p-8">
                  Aucun utilisateur trouvé
                </td>
              </tr>
            ) : (
              users.map((user) => {
                const badge = getRoleBadge(user.role)
                return (
                  <tr key={user.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="avatar avatar-sm">
                          {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                        </div>
                        <span className="font-medium">
                          {user.firstName} {user.lastName}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted" />
                        {user.email}
                      </div>
                    </td>
                    <td>
                      <span className={badge.class}>{badge.label}</span>
                    </td>
                    <td className="text-muted">
                      {new Date(user.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          className="btn-icon btn-sm btn-ghost"
                          onClick={() => openEditModal(user)}
                          title="Modifier le rôle"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          className="btn-icon btn-sm btn-ghost"
                          onClick={() => openDeleteModal(user)}
                          title="Supprimer l'utilisateur"
                        >
                          <Trash2 className="h-4 w-4 text-danger" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modale Ajout Utilisateur - Popup Central */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={resetForm}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Ajouter un utilisateur</h3>
              <button className="btn-icon btn-sm btn-ghost" onClick={resetForm}>
                <X className="h-4 w-4" />
              </button>
            </div>

            {tempPassword ? (
              <div className="modal-body">
                <div className="alert alert-success">
                  <h4 className="font-semibold mb-2">Utilisateur créé avec succès !</h4>
                  <p className="mb-2">
                    Mot de passe temporaire : <strong className="font-mono text-lg">{tempPassword}</strong>
                  </p>
                  <p className="text-sm text-muted">
                    Veuillez noter ce mot de passe et le transmettre à l'utilisateur de manière sécurisée.
                  </p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={resetForm}>
                    Fermer
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleAddUser}>
                <div className="modal-body space-y-4">
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Prénom *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        required
                        minLength={2}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Nom *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        required
                        minLength={2}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Rôle *</label>
                    <select
                      className="form-select"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      required
                    >
                      <option value="collaborateur">Utilisateur</option>
                      <option value="enseignant">Enseignant</option>
                      <option value="responsable">Responsable</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-ghost" onClick={resetForm}>
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Créer l'utilisateur
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modale Modifier Rôle - Popup Central */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Modifier le rôle</h3>
              <button className="btn-icon btn-sm btn-ghost" onClick={() => setShowEditModal(false)}>
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="modal-body">
              <p className="mb-4">
                Modifier le rôle de <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> :
              </p>

              <div className="space-y-2">
                {['collaborateur', 'enseignant', 'responsable', 'super_admin'].map((role) => (
                  <label key={role} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="role"
                      value={role}
                      checked={selectedUser.role === role}
                      onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
                      className="form-radio"
                    />
                    <div>
                      <div className="font-medium">
                        {role === 'collaborateur' && 'Utilisateur'}
                        {role === 'enseignant' && 'Enseignant'}
                        {role === 'responsable' && 'Responsable'}
                        {role === 'super_admin' && 'Super Admin'}
                      </div>
                      <div className="text-sm text-muted">
                        {role === 'collaborateur' && 'Accès de base aux fonctionnalités'}
                        {role === 'enseignant' && 'Gestion des projets et évaluations'}
                        {role === 'responsable' && 'Gestion de l\'équipe et administration'}
                        {role === 'super_admin' && 'Accès complet à toutes les fonctionnalités'}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowEditModal(false)}>
                Annuler
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleUpdateRole(selectedUser.id, selectedUser.role)}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Suppression - Popup Central Moderne */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header avec gradient */}
            <div className="p-8 bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Supprimer l'utilisateur</h3>
                    <p className="text-sm text-red-600 mt-1">Action irréversible</p>
                  </div>
                </div>
                <button className="btn-icon btn-sm btn-ghost hover:bg-red-100" onClick={() => setShowDeleteModal(false)}>
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body avec plus de padding */}
            <div className="p-8">
              {/* User info */}
              <div className="mb-6">
                <div className="bg-white rounded-lg p-5 border-2 border-gray-200">
                  <p className="font-semibold text-gray-900 text-base">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </p>
                  <p className="text-sm text-gray-600">{selectedUser.email}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Rôle : <span className="font-medium">{getRoleBadge(selectedUser.role).label}</span>
                  </p>
                </div>
              </div>

              {/* Loading dependencies */}
              {loadingDependencies && (
                <div className="mb-6 p-6 bg-blue-50 border-2 border-blue-200 rounded-xl text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-blue-700 mt-3">Chargement des dépendances...</p>
                </div>
              )}

              {/* Dependencies found */}
              {!loadingDependencies && dependencies && dependencies.total > 0 && (
                <div className="mb-6 space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-yellow-900 text-sm mb-1">
                        Cet utilisateur a {dependencies.total} dépendance(s)
                      </p>
                      <p className="text-xs text-yellow-700">
                        Veuillez choisir comment gérer chaque type de dépendance avant de supprimer l'utilisateur.
                      </p>
                    </div>
                  </div>

                  {/* Projects dependencies */}
                  {dependencies.projects.count > 0 && (
                    <div className="p-5 bg-blue-50 border-2 border-blue-200 rounded-xl">
                      <div className="flex items-center gap-2 mb-3">
                        <UsersIcon className="h-5 w-5 text-blue-600" />
                        <h4 className="font-semibold text-blue-900">
                          {dependencies.projects.count} Projet(s)
                        </h4>
                      </div>

                      {dependencies.projects.items && dependencies.projects.items.length > 0 && (
                        <div className="mb-3 p-3 bg-white rounded-lg border border-blue-200">
                          <p className="text-xs text-gray-600 mb-2">Exemples :</p>
                          <ul className="text-xs text-gray-700 space-y-1">
                            {dependencies.projects.items.slice(0, 5).map((item, idx) => (
                              <li key={idx}>• {item.project?.title || 'Projet sans titre'}</li>
                            ))}
                            {dependencies.projects.count > 5 && (
                              <li className="text-gray-500 italic">... et {dependencies.projects.count - 5} autre(s)</li>
                            )}
                          </ul>
                        </div>
                      )}

                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="projectsAction"
                            value="reassign"
                            checked={dependencyActions.projects.action === 'reassign'}
                            onChange={(e) => setDependencyActions(prev => ({
                              ...prev,
                              projects: { ...prev.projects, action: 'reassign' }
                            }))}
                            className="form-radio text-blue-600"
                          />
                          <span className="font-medium text-gray-900">Réassigner à :</span>
                        </label>

                        {dependencyActions.projects.action === 'reassign' && (
                          <select
                            value={dependencyActions.projects.targetUserId}
                            onChange={(e) => setDependencyActions(prev => ({
                              ...prev,
                              projects: { ...prev.projects, targetUserId: e.target.value }
                            }))}
                            className="form-select w-full text-sm ml-6"
                          >
                            <option value="">-- Sélectionner un utilisateur --</option>
                            {availableUsers.map(u => (
                              <option key={u.id} value={u.id}>
                                {u.firstName} {u.lastName} ({u.email})
                              </option>
                            ))}
                          </select>
                        )}

                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="projectsAction"
                            value="delete"
                            checked={dependencyActions.projects.action === 'delete'}
                            onChange={(e) => setDependencyActions(prev => ({
                              ...prev,
                              projects: { ...prev.projects, action: 'delete' }
                            }))}
                            className="form-radio text-red-600"
                          />
                          <span className="font-medium text-red-900">Supprimer les projets</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Annotations dependencies */}
                  {dependencies.annotations.count > 0 && (
                    <div className="p-5 bg-purple-50 border-2 border-purple-200 rounded-xl">
                      <div className="flex items-center gap-2 mb-3">
                        <Mail className="h-5 w-5 text-purple-600" />
                        <h4 className="font-semibold text-purple-900">
                          {dependencies.annotations.count} Annotation(s)
                        </h4>
                      </div>

                      {dependencies.annotations.items && dependencies.annotations.items.length > 0 && (
                        <div className="mb-3 p-3 bg-white rounded-lg border border-purple-200">
                          <p className="text-xs text-gray-600 mb-2">Exemples :</p>
                          <ul className="text-xs text-gray-700 space-y-1">
                            {dependencies.annotations.items.slice(0, 5).map((item, idx) => (
                              <li key={idx}>• {item.content?.substring(0, 50) || 'Annotation'}...</li>
                            ))}
                            {dependencies.annotations.count > 5 && (
                              <li className="text-gray-500 italic">... et {dependencies.annotations.count - 5} autre(s)</li>
                            )}
                          </ul>
                        </div>
                      )}

                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="annotationsAction"
                            value="reassign"
                            checked={dependencyActions.annotations.action === 'reassign'}
                            onChange={(e) => setDependencyActions(prev => ({
                              ...prev,
                              annotations: { ...prev.annotations, action: 'reassign' }
                            }))}
                            className="form-radio text-purple-600"
                          />
                          <span className="font-medium text-gray-900">Réassigner à :</span>
                        </label>

                        {dependencyActions.annotations.action === 'reassign' && (
                          <select
                            value={dependencyActions.annotations.targetUserId}
                            onChange={(e) => setDependencyActions(prev => ({
                              ...prev,
                              annotations: { ...prev.annotations, targetUserId: e.target.value }
                            }))}
                            className="form-select w-full text-sm ml-6"
                          >
                            <option value="">-- Sélectionner un utilisateur --</option>
                            {availableUsers.map(u => (
                              <option key={u.id} value={u.id}>
                                {u.firstName} {u.lastName} ({u.email})
                              </option>
                            ))}
                          </select>
                        )}

                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="annotationsAction"
                            value="delete"
                            checked={dependencyActions.annotations.action === 'delete'}
                            onChange={(e) => setDependencyActions(prev => ({
                              ...prev,
                              annotations: { ...prev.annotations, action: 'delete' }
                            }))}
                            className="form-radio text-red-600"
                          />
                          <span className="font-medium text-red-900">Supprimer les annotations</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Mentions note */}
                  {dependencies.mentions.count > 0 && (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-xs text-gray-600">
                        <strong>Note :</strong> {dependencies.mentions.count} mention(s) seront automatiquement supprimées.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* No dependencies */}
              {!loadingDependencies && dependencies && dependencies.total === 0 && (
                <div className="mb-6">
                  <div className="flex items-start gap-4 p-6 bg-red-50 border-2 border-red-200 rounded-xl">
                    <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-900 text-lg mb-2">
                        Attention : Cette action est irréversible
                      </p>
                      <p className="text-sm text-red-700">
                        Êtes-vous sûr de vouloir supprimer définitivement cet utilisateur ?
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer avec buttons plus espacés */}
            <div className="px-8 py-6 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-2xl">
              <button className="btn btn-ghost px-6 py-3" onClick={() => setShowDeleteModal(false)}>
                Annuler
              </button>
              <button
                className="btn btn-danger px-6 py-3 flex items-center gap-2"
                onClick={handleDeleteUser}
                disabled={loadingDependencies}
              >
                <Trash2 className="h-4 w-4" />
                {loadingDependencies ? 'Chargement...' :
                 dependencies && dependencies.total > 0 ? 'Gérer et supprimer' :
                 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Upgrade Plan - Popup Central Moderne */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header avec gradient */}
            <div className="p-8 bg-gradient-to-r from-yellow-50 via-orange-50 to-amber-50 border-b border-yellow-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                    <Crown className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {hasRole(['super_admin', 'responsable']) ? 'Passez au plan supérieur' : 'Limite atteinte'}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">Débloquez plus d'utilisateurs et de fonctionnalités</p>
                  </div>
                </div>
                <button className="btn-icon btn-sm btn-ghost hover:bg-yellow-100" onClick={() => setShowUpgradeModal(false)}>
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body avec plus de padding */}
            <div className="p-8">
              {/* Alerte limite atteinte */}
              <div className="mb-8 p-6 bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Crown className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-lg mb-1">
                      Vous avez atteint la limite de votre plan {planLimit.name}
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>{planLimit.maxUsers} utilisateur{planLimit.maxUsers > 1 ? 's' : ''}</strong> maximum
                    </p>
                  </div>
                </div>
              </div>

              {hasRole(['super_admin', 'responsable']) ? (
                <div>
                  <p className="text-gray-700 mb-6 text-center text-base">
                    Choisissez le plan qui correspond à vos besoins et augmentez votre capacité d'utilisateurs
                  </p>

                  {/* Grille des plans - Basée sur /pricing */}
                  <div className="grid md:grid-cols-3 gap-6 mb-6">
                    {/* Pack Basique */}
                    <button
                      onClick={() => window.location.href = '/pricing'}
                      className="text-left p-6 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl hover:border-blue-400 hover:shadow-xl transition-all duration-300 group"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Users className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2">Pack Basique</h4>
                      <p className="text-sm text-gray-600 mb-4">Idéal pour les petites équipes</p>
                      <div className="mb-4">
                        <span className="text-3xl font-bold text-gray-900">250€</span>
                        <span className="text-sm text-gray-600">/mois</span>
                      </div>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>Jusqu'à 10 utilisateurs</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>Modules Métier, Chaîne, Trame</span>
                        </div>
                      </div>
                      <div className="text-center mt-4 text-sm font-semibold text-blue-600 group-hover:text-blue-700">
                        En savoir plus →
                      </div>
                    </button>

                    {/* Pack Standard - Recommandé */}
                    <button
                      onClick={() => window.location.href = '/pricing'}
                      className="text-left p-6 border-2 border-purple-400 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl hover:border-purple-500 hover:shadow-xl transition-all duration-300 group relative ring-2 ring-purple-200"
                    >
                      <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-xl">
                        POPULAIRE
                      </div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Zap className="h-6 w-6 text-purple-600" />
                        </div>
                      </div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2">Pack Standard</h4>
                      <p className="text-sm text-gray-600 mb-4">Le plus complet pour les équipes actives</p>
                      <div className="mb-4">
                        <span className="text-3xl font-bold text-gray-900">400€</span>
                        <span className="text-sm text-gray-600">/mois</span>
                      </div>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>Jusqu'à 30 utilisateurs</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>6 modules inclus + Navette</span>
                        </div>
                      </div>
                      <div className="text-center mt-4 text-sm font-semibold text-purple-600 group-hover:text-purple-700">
                        En savoir plus →
                      </div>
                    </button>

                    {/* Pack Premium */}
                    <button
                      onClick={() => window.location.href = '/pricing'}
                      className="text-left p-6 border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl hover:border-green-400 hover:shadow-xl transition-all duration-300 group"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Rocket className="h-6 w-6 text-green-600" />
                        </div>
                      </div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2">Pack Premium</h4>
                      <p className="text-sm text-gray-600 mb-4">Solution complète pour grandes structures</p>
                      <div className="mb-4">
                        <span className="text-3xl font-bold text-gray-900">900€</span>
                        <span className="text-sm text-gray-600">/mois</span>
                      </div>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>Jusqu'à 50 utilisateurs</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>Tous les modules inclus</span>
                        </div>
                      </div>
                      <div className="text-center mt-4 text-sm font-semibold text-green-600 group-hover:text-green-700">
                        En savoir plus →
                      </div>
                    </button>
                  </div>

                  {/* Note économies annuelles */}
                  <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      💰 <strong>Économisez 15%</strong> en choisissant un paiement annuel
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-4 p-6 bg-blue-50 border-2 border-blue-200 rounded-xl">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Send className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-blue-900 mb-2 text-lg">
                      Contactez votre administrateur
                    </p>
                    <p className="text-sm text-blue-700 mb-4">
                      Seul l'administrateur de votre organisation peut passer à un plan supérieur.
                      Souhaitez-vous lui envoyer une demande ?
                    </p>
                    <p className="text-xs text-blue-600">
                      Un email sera envoyé automatiquement à votre administrateur
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer avec buttons plus espacés */}
            <div className="px-8 py-6 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-2xl">
              <button className="btn btn-ghost px-6 py-3" onClick={() => setShowUpgradeModal(false)}>
                Annuler
              </button>
              {hasRole(['super_admin', 'responsable']) ? (
                <button className="btn btn-primary px-6 py-3 flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700" onClick={handleUpgradePlan}>
                  <Crown className="h-5 w-5" />
                  Voir tous les plans
                </button>
              ) : (
                <button className="btn btn-primary px-6 py-3 flex items-center gap-2" onClick={handleUpgradePlan}>
                  <Send className="h-5 w-5" />
                  Envoyer la demande
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
